import { useState, useEffect } from 'react';
import { getSupabaseClient } from '../supabaseClient';

/**
 * Hook to fetch locations associated with the current user/account
 * @param {string} businessType - The business type to use for the supabase client
 * @param {boolean} ignoreCache - Whether to bypass cache and fetch fresh data
 * @returns {Object} - Object containing locations array and loading state
 */
export const useLocations = (businessType, ignoreCache = false) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const supabase = getSupabaseClient(businessType);
  
  // Store cache key in state to avoid deps issues
  const [cacheKey] = useState(`locations_${businessType}`);
  
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Check cache first if not ignoring
        if (!ignoreCache) {
          const cachedData = sessionStorage.getItem(cacheKey);
          if (cachedData) {
            const { data, timestamp } = JSON.parse(cachedData);
            
            // Use cache if it's less than 5 minutes old
            const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
            if (timestamp > fiveMinutesAgo) {
              setLocations(data);
              setLoading(false);
              return;
            }
          }
        }
        
        // Get the current user
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError) throw userError;
        
        // Fetch locations
        const { data, error } = await supabase
          .from('vapi_location_settings')
          .select('id, name, google_address, google_places_id, ivr_order')
          .eq('account_id', user.id)
          .order('ivr_order', { ascending: true, nullsLast: true });
          
        if (error) throw error;
        
        // Sort locations: first by ivr_order if present, then by name
        const sortedLocations = [...data].sort((a, b) => {
          if (a.ivr_order && b.ivr_order) {
            return parseInt(a.ivr_order) - parseInt(b.ivr_order);
          }
          if (a.ivr_order) return -1;
          if (b.ivr_order) return 1;
          return a.name.localeCompare(b.name);
        });
        
        // Cache the result
        sessionStorage.setItem(
          cacheKey, 
          JSON.stringify({
            data: sortedLocations,
            timestamp: Date.now()
          })
        );
        
        setLocations(sortedLocations);
      } catch (err) {
        console.error('Error fetching locations:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchLocations();
  }, [supabase, cacheKey, ignoreCache, businessType]);
  
  return { locations, loading, error };
};

export default useLocations; 